import { AxiosStatusCode } from 'smg/axios';
import { SetBootstrapVersion } from 'smg/bootstrap';
import { GetCartID } from 'smg/customer-api/v2/carts';
import { GetPageRequestID } from 'smg/customer-api/pagerequests';
import * as accounts from 'smg/customer-api/accounts';
import * as session from 'smg/customer-api/session';
import * as sessionGuard from 'smg/customer-api/session-guard';
import * as enhancedconversions from 'smg/enhanced-conversions';
import * as errors from 'smg/errors';
import { BindFancybox } from 'smg/fancybox';
import * as html from 'smg/html';
import { SetIconLibrary } from 'smg/icons';
import * as meta from 'smg/meta';
import * as modals from 'smg/modal';
import * as paypal from 'smg/customer-api/paypal';
import QueryString from 'smg/querystring';
import * as carts from './carts';
import HolidayBanners from './holiday-banners';

import 'smg/growthbook';

// These behaviors span many layouts and shortcodes rather than single pages, so
// they can't be page modules — but each is driven entirely by its own selectors,
// and together they drag lineitems/models, luxon and swiper onto every page.
// Lazy-load each only when the DOM it acts on is present, so a page without a
// quote form or a carousel never downloads it. Each gate must be a superset of the
// selectors the module queries at import time.
if (document.querySelector('.site-navbar-toggler')) {
    void import('./mobile-menu').then(mobileMenu => mobileMenu.default());
}

if (document.querySelector('.site-navbar-menu-toggle')) {
    void import('./mega-menu').then(megaMenu => megaMenu.default());
}

if (document.querySelector('.coin-option-preview-group')) {
    void import('./coin-option-previews').then(coinOptionPreviews => coinOptionPreviews.default());
}

if (document.querySelector('.banner-quote-form')) {
    void import('./banner-quote-form');
}

if (document.querySelector('.mini-quote-form')) {
    void import('./mini-quote-form');
}

if (document.querySelector('form#design-template-download-form-cc, #design-template-download-form-cc form')) {
    void import('./design-template');
}

if (document.querySelector('.swiper.gallery-swiper,.swiper.logo-swiper,.swiper.no-loop')) {
    void import('./swiper-init');
}

const $galleryQuoteTemplate = document.querySelector('#gallery-quote-template') as HTMLTemplateElement;
const $galleryQuoteContainers = Array.from(document.querySelectorAll<HTMLElement>('.gallery-images-container'));
if ($galleryQuoteTemplate && $galleryQuoteContainers.length) {
    void import('./gallery-quote').then(galleryQuote => {
        for (const $container of $galleryQuoteContainers) {
            galleryQuote.default($container, $galleryQuoteTemplate, $container.dataset.formName || 'gallery');
        }
    });
}

declare interface myWindow extends Window {
    redditPixel?: string;
    metaPixel?: string;
}
declare const window: myWindow;

SetIconLibrary('fontAwesome5');
SetBootstrapVersion(5);

modals.SetDefaults({
    cancelButtonClass: 'btn btn-outline-secondary',
    confirmButtonClass: 'btn btn-secondary',
    closeButtonClass: 'd-none',
    // animation: true,
});

BindFancybox();

errors.Defaults.showErrorFunc = msg => new modals.default({
    body: html.FromString(`<div>
            <i class="fas fa-exclamation-circle w-100 text-center mb-5 fa-5x text-danger"></i>
            <div class="fs-4 text-center">${html.Escape(msg)}</div>
        </div>`),
    cancelText: 'Close',
});

// Token the last paintSession ran under — used by logout + storage-event
// guards so a cross-tab account swap repaints instead of logging out the
// wrong user or acting on a stale session response.
let currentToken: string | null = null;
// User id painted under currentToken — logout removes this entry from the
// stored account list. Set only on the logged-in path.
let currentUserID: string | null = null;

function clearInjectedAccounts() {
    document.querySelectorAll('.nav-account-injected').forEach(el => el.remove());
    document.querySelector('#nav-accounts-header')?.classList.add('d-none');
}

// The active account is named in three places — the header panel, the desktop
// account sidebar and the mobile Account drill-down — all from this one
// source, so the switcher rows and the "you are here" line can't disagree.
// Someone with no name shows their company, and failing that their email,
// which then isn't repeated on the second line.
function paintIdentity(name: string, email: string) {
    document.querySelectorAll('.nav-name').forEach(el => {
        el.textContent = name;
    });
    document.querySelectorAll('.nav-email').forEach(el => {
        el.textContent = email === name ? '' : email;
    });
}

function paintSession() {
    // The account icon and its toggle are session-independent, so nothing out in
    // the bar is painted here — only which of the panel's two lists is showing.
    const navGuest = document.querySelector('#nav-account-guest');
    const navSession = document.querySelector('#nav-account-session');
    // Two mobile rows, one per session state: a Login link, and the Account
    // drill-down holding the account pages.
    const mobileLogin = document.querySelector('#mobile-login');
    const mobileAccountMenu = document.querySelector('#mobile-account-menu');
    const mobileLogout = document.querySelector('#mobile-logout');
    if (!navGuest || !navSession) {
        return;
    }

    // Captured before the session request so the account entry records the
    // token this response actually belongs to — another tab (or a fast
    // "Add account" login) can swap the live token mid-request.
    const loginToken = session.GetLoginToken();

    if (loginToken) {
        session.Session().then(sess => {
            // The live token moved while this request was in flight (another
            // tab or a fast "Add account" login) — this response describes
            // the OLD account, so don't let it paint the header. Repaint
            // under the current token instead of a full reload (reload was
            // only needed on initial page load; re-entrant paint is enough).
            if (session.GetLoginToken() !== loginToken) {
                paintSession();
                return;
            }

            // Token and user id become current together, only once the
            // session resolves. Setting the token earlier would leave a
            // window pairing the NEW token with the OLD user id, where
            // logout deletes the new account's session while evicting the
            // old account from the stored list.
            currentToken = loginToken;
            currentUserID = sess.user.id;
            paintIdentity(sess.user.fullName() || sess.user.company || sess.user.emailAddress.email, sess.user.emailAddress.email);

            navSession.classList.remove('d-none');
            navGuest.classList.add('d-none');

            mobileLogin?.classList.add('d-none');
            mobileAccountMenu?.classList.remove('d-none');
            mobileLogout?.classList.remove('d-none');

            accounts.Remember({
                token: loginToken,
                userID: sess.user.id,
                name: sess.user.fullName() || sess.user.company || sess.user.emailAddress.email,
                email: sess.user.emailAddress.email,
            });

            renderAccountSwitcher();
        }).catch(err => {
            // A token that won't resolve is no better than none for what the
            // panel can offer, so fall back to the logged-out list. A re-entrant
            // paint can be undoing a previous paint that succeeded, so the mobile
            // rows have to come back too.
            navGuest.classList.remove('d-none');
            navSession.classList.add('d-none');
            mobileLogin?.classList.remove('d-none');
            mobileAccountMenu?.classList.add('d-none');
            mobileLogout?.classList.add('d-none');
            paintIdentity('', '');
            throw err;
        });
    } else {
        currentToken = null;
        currentUserID = null;
        navGuest.classList.remove('d-none');
        navSession.classList.add('d-none');
        mobileLogin?.classList.remove('d-none');
        mobileAccountMenu?.classList.add('d-none');
        mobileLogout?.classList.add('d-none');
        paintIdentity('', '');
        clearInjectedAccounts();
    }
}

function renderAccountSwitcher() {
    const $template = document.querySelector('#nav-account-item') as HTMLTemplateElement | null;
    const $addAccount = document.querySelector('#nav-add-account');
    const $header = document.querySelector('#nav-accounts-header');
    const $mobileAccounts = document.querySelector('#mobile-accounts');
    if (!$template || !$addAccount) {
        return;
    }

    // Idempotent re-render: drop prior injections before re-building so a
    // cross-tab repaint doesn't stack duplicate rows.
    clearInjectedAccounts();

    let rendered = 0;
    // One lock for the whole menu: rapid clicks on two DIFFERENT accounts
    // would otherwise race two switch requests, letting response order (not
    // the user's final click) decide the active account.
    let switching = false;
    for (const account of accounts.List()) {
        if (accounts.IsCurrent(account)) {
            continue;
        }

        const $fragment = $template.content.cloneNode(true) as DocumentFragment;
        const $li = $fragment.querySelector('li') as HTMLLIElement;
        $li.classList.add('nav-account-injected');
        const $name = $li.querySelector('.account-name') as HTMLElement;
        const $email = $li.querySelector('.account-email') as HTMLElement;
        const $a = $li.querySelector('a') as HTMLAnchorElement;
        $name.textContent = account.name;
        $email.textContent = account.email;

        let $mobileLi: HTMLLIElement | undefined;
        const onSwitch = (e: Event) => {
            e.preventDefault();
            if (switching) {
                return;
            }
            switching = true;
            void accounts.Switch(account).then(() => {
                window.location.reload();
            }).catch((err: unknown) => {
                // Switch only evicts the stored account on 404 (dead/rotated
                // token); a transient failure keeps the entry so it stays
                // clickable.
                switching = false;
                // A dead token (404) evicts the account — the row vanishing
                // IS the feedback, so an error modal on top of it would
                // undo the graceful part of graceful eviction. Modals are
                // for failures the customer can act on by retrying.
                if (AxiosStatusCode(err) === 404) {
                    $li.remove();
                    $mobileLi?.remove();
                    rendered--;
                    if (rendered === 0) {
                        $header?.classList.add('d-none');
                    }
                    return;
                }
                errors.ShowError(err);
            });
        };
        $a.addEventListener('click', onSwitch);
        $addAccount.parentElement?.insertBefore($li, $addAccount);
        rendered++;

        if ($mobileAccounts?.parentElement) {
            // These rows land in the same `<ul>` that becomes the desktop nav at
            // lg+, so they need `d-lg-none` — otherwise an account row would
            // show up in the header bar beside Products and Gallery.
            $mobileLi = document.createElement('li');
            $mobileLi.className = 'nav-item d-lg-none nav-account-injected';
            const $mobileA = document.createElement('a');
            $mobileA.className = 'nav-link';
            $mobileA.href = '#';
            // The mobile card has no "Switch account" heading to sit under — it
            // is one flat list of destinations — so the row has to say what it
            // does on its own. A bare name reads as a page you'd navigate to.
            const $mobileLabel = document.createElement('span');
            $mobileLabel.textContent = `Switch to ${account.name}`;
            $mobileA.appendChild($mobileLabel);
            // Same second line the desktop rows carry: two accounts for one
            // person differ only by email once `fullName()` matches.
            if (account.email) {
                const $mobileEmail = document.createElement('span');
                // `fw-normal` because the mobile rows are bold by default —
                // the email is the subordinate line here as it is on desktop.
                $mobileEmail.className = 'd-block small text-muted fw-normal';
                $mobileEmail.textContent = account.email;
                $mobileA.appendChild($mobileEmail);
            }
            $mobileA.addEventListener('click', onSwitch);
            $mobileLi.appendChild($mobileA);
            $mobileAccounts.parentElement.insertBefore($mobileLi, $mobileAccounts);
        }
    }

    if (rendered > 0) {
        $header?.classList.remove('d-none');
    }
}

// Single delegated logout listener — re-entrant paint must not stack per-
// element handlers, and a cross-tab token swap should repaint rather than
// log out the account this tab last painted.
document.addEventListener('click', e => {
    const $logout = (e.target as Element).closest('.logout');
    if (!$logout) {
        return;
    }
    e.preventDefault();
    // Another tab may have swapped accounts since this page painted —
    // logging out then would clear the NEW account's session while
    // evicting the old one from the stored list. (No storage-event reload
    // on purpose: a spontaneous reload could destroy in-progress form
    // input in this tab.)
    if (session.GetLoginToken() !== currentToken) {
        paintSession();
        return;
    }
    const userID = currentUserID;
    if (userID) {
        accounts.Remove(userID);
    }
    session.DeleteSession();
    window.location.reload();
});

function initCart() {
    if (!GetCartID()) {
        return;
    }
    carts.GetCartCount().then(count => {
        if (!count) {
            return;
        }

        carts.IncrementCartCount(count);
    }).catch(err => console.error(`failed to get cart count: ${String(err)}`));
}

errors.InitClientSideErrorLogging();

async function main() {
    await session.ConsumeLoginTokenFromURL();
    // Page tracking starts after the exchange so the page request belongs to the
    // account the link signed in, not whoever was signed in before.
    void GetPageRequestID();

    enhancedconversions.Hydrate();

    // After URL-login so pageToken reflects the token this page rendered under.
    sessionGuard.Init({ onSoftChange: paintSession });

    if (QueryString().get('paypal') === 'success') {
        const successAlert = document.querySelector('#paypal-success') as HTMLElement;
        successAlert.classList.remove('d-none');
        successAlert.addEventListener('closed.bs.alert', () => {
            successAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    if (QueryString().get('paypal') === 'error') {
        const successAlert = document.querySelector('#paypal-error') as HTMLElement;
        successAlert.classList.remove('d-none');
        successAlert.addEventListener('closed.bs.alert', () => {
            successAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    const paypalRetryURL = paypal.DeclinedRetryURL();
    if (paypalRetryURL) {
        const declinedAlert = document.querySelector('#paypal-declined') as HTMLElement;
        (declinedAlert.querySelector('#paypal-declined-retry') as HTMLAnchorElement).href = paypalRetryURL;
        declinedAlert.classList.remove('d-none');
        declinedAlert.addEventListener('closed.bs.alert', () => {
            declinedAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    if (QueryString().get('deleteCart')) {
        void carts.DeleteCart();
        QueryString().delete('deleteCart');
    }

    paintSession();
    initCart();
    HolidayBanners();

    // Initialize Reddit pixel tracking
    if (window.rdt) {
        const rdt = window.rdt;
        if (session.GetLoginToken()) {
            void session.Session().then(sess => {
                rdt('init', window.redditPixel, {
                    'email': sess.user.emailAddress.email,
                    'phoneNumber': sess.user.phoneNumber.phone,
                    'externalId': sess.user.id,
                });
            }).catch(() => {
                // If session fails, initialize without user data
                rdt('init', window.redditPixel);
            }).finally(() => {
                rdt('track', 'PageVisit');
            });
        } else {
            rdt('init', window.redditPixel);
            rdt('track', 'PageVisit');
        }
    }

    // Initialize Meta (Facebook) pixel tracking
    if (window.metaPixel) {
        const pixelId = window.metaPixel;
        void session.Session().then(sess => {
            meta.InitializeMeta(pixelId, sess ? {
                email: sess.user.emailAddress.email,
                phone: sess.user.phoneNumber.phone,
                userId: sess.user.id,
            } : undefined);
        }).catch(() => {
            meta.InitializeMeta(pixelId);
        }).finally(() => {
            meta.TrackPageView();
        });
    }
}

void main();
